import { FunctionComponent } from 'react';
import { Button, Collapse, Select, Tag, Tooltip, Typography } from 'antd';
import {
    CalendarOutlined,
    DeleteOutlined,
    EditOutlined,
    EyeOutlined,
    FileTextOutlined,
    PlusCircleOutlined,
    PlusOutlined,
    ReadOutlined,
    SaveOutlined,
    TeamOutlined,
    TrophyOutlined,
} from '@ant-design/icons';
import { CollapseProps } from 'antd/lib';
import { MaClasseViewProps } from './types';
import { SelectFormItem } from '../../../components/common/SelectFormItem';
import { getResultatOptions } from '../../../components/common/commoninputs';
import dayjs from 'dayjs';
import { APPLICATION_DATE_FORMAT, firstLettertoUpperCase } from '../../../utils/FormUtils';

/** Génère une couleur d'avatar déterministe à partir du nom */
function getAvatarColor(name: string): string {
    const colors = [
        '#4FC3F7', '#81C784', '#FFB74D', '#F06292',
        '#BA68C8', '#4DB6AC', '#7986CB', '#A1887F',
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
}

export const MaClasseMobileView: FunctionComponent<MaClasseViewProps> = ({
    classe,
    elevesEnriched,
    feuillesPresence,
    bulletins,
    selectedEleveId,
    isAdmin,
    onCreateFeuillePresence,
    onViewFeuille,
    onDeleteFeuille,
    onModifierResultat,
    onEnregistrerResultat,
    loadBulletinsEleve,
    onCreerBulletin,
    onModifierBulletin,
    onDeleteBulletin,
    getBulletinPdfButton,
    getTauxReussite,
    getJourClasse,
}) => {

    // ─── Section Effectif ──────────────────────────────────────────────────────
    const renderEffectif = () => (
        <div className="ma-classe-mobile-section">
            {elevesEnriched.length === 0 ? (
                <div className="ma-classe-mobile-empty">Aucun élève dans cette classe</div>
            ) : (
                elevesEnriched.map((eleve) => {
                    const initiales = `${eleve.prenom.charAt(0)}${eleve.nom.charAt(0)}`.toUpperCase();
                    const avatarColor = getAvatarColor(eleve.nom + eleve.prenom);
                    return (
                        <div className="utilisateur-card-mobile" key={eleve.id}>
                            <div className="utilisateur-card-header">
                                <div
                                    className="utilisateur-card-avatar"
                                    style={{ backgroundColor: avatarColor }}
                                >
                                    {initiales}
                                </div>
                                <div className="utilisateur-card-identity">
                                    <Typography.Text strong className="utilisateur-card-name">
                                        {eleve.prenom} {eleve.nom}
                                    </Typography.Text>
                                    <Typography.Text type="secondary" className="utilisateur-card-username">
                                        {eleve.dateNaissance}
                                    </Typography.Text>
                                </div>
                                <div className="utilisateur-card-status-badges">
                                    <Tag color="geekblue" style={{ fontSize: '11px', margin: 0 }}>
                                        {eleve.niveauInterne}
                                    </Tag>
                                </div>
                            </div>
                            <div className="utilisateur-card-body">
                                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', paddingTop: '6px' }}>
                                    <Tag color={eleve.autorisationMedia ? 'green' : 'red'} style={{ fontSize: '11px' }}>
                                        Photos {eleve.autorisationMedia ? 'OK' : 'Non'}
                                    </Tag>
                                    <Tag color={eleve.autorisationAutonomie ? 'green' : 'red'} style={{ fontSize: '11px' }}>
                                        Rentre seul {eleve.autorisationAutonomie ? 'Oui' : 'Non'}
                                    </Tag>
                                </div>
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );

    // ─── Section Présences ─────────────────────────────────────────────────────
    const renderPresences = () => (
        <div className="ma-classe-mobile-section">
            <Button
                type="primary"
                icon={<PlusCircleOutlined />}
                onClick={onCreateFeuillePresence}
                block
                style={{ marginBottom: '12px' }}
            >
                Nouvelle feuille de présence
            </Button>
            {feuillesPresence.length === 0 ? (
                <div className="ma-classe-mobile-empty">Aucune feuille de présence</div>
            ) : (
                feuillesPresence.map((feuille) => {
                    const absents = feuille.presenceEleves.filter((p) => !p.present);
                    const presents = feuille.presenceEleves.filter((p) => p.present);
                    const dateLabel = dayjs(feuille.date, APPLICATION_DATE_FORMAT).format(APPLICATION_DATE_FORMAT);

                    return (
                        <div className="ma-classe-presence-card" key={feuille.id}>
                            <div className="ma-classe-presence-card-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CalendarOutlined style={{ color: '#4FC3F7' }} />
                                    <strong style={{ fontSize: '14px' }}>{dateLabel}</strong>
                                </div>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <Tooltip title="Voir la feuille" color="geekblue">
                                        <Button
                                            size="small"
                                            icon={<EyeOutlined />}
                                            onClick={() => onViewFeuille(feuille, true)}
                                        />
                                    </Tooltip>
                                    <Button
                                        size="small"
                                        type="primary"
                                        icon={<EditOutlined />}
                                        onClick={() => onViewFeuille(feuille, false)}
                                    />
                                    <Button
                                        size="small"
                                        type="primary"
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={() => onDeleteFeuille(feuille)}
                                    />
                                </div>
                            </div>
                            <div className="ma-classe-presence-card-body">
                                <span>
                                    <Tag color="green">{presents.length} présent{presents.length !== 1 ? 's' : ''}</Tag>
                                </span>
                                <span>
                                    <Tag color="red">{absents.length} absent{absents.length !== 1 ? 's' : ''}</Tag>
                                </span>
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );

    // ─── Section Bulletins ─────────────────────────────────────────────────────
    const renderBulletins = () => (
        <div className="ma-classe-mobile-section">
            <div style={{ marginBottom: '10px' }}>
                <SelectFormItem
                    name="eleveid"
                    label="Élève"
                    options={elevesEnriched.map((eleve) => ({
                        value: eleve.id,
                        label: `${eleve.prenom} ${eleve.nom}`,
                    }))}
                    onChange={loadBulletinsEleve}
                />
            </div>
            <Button
                icon={<PlusOutlined />}
                type="primary"
                disabled={!selectedEleveId}
                onClick={onCreerBulletin}
                block
                style={{ marginBottom: '12px' }}
            >
                Créer un bulletin
            </Button>
            {!selectedEleveId ? (
                <div className="ma-classe-mobile-empty">Sélectionnez un élève pour voir ses bulletins</div>
            ) : bulletins.length === 0 ? (
                <div className="ma-classe-mobile-empty">Aucun bulletin pour cet élève</div>
            ) : (
                bulletins.map((bulletin) => {
                    const isLocked = bulletin.complet === true && !isAdmin;
                    const tooltipTitle = isLocked ? 'Bulletin complet, modification réservée aux administrateurs' : '';
                    return (
                        <div className="ma-classe-bulletin-card" key={bulletin.id}>
                            <div className="ma-classe-bulletin-card-header">
                                <strong>
                                    {firstLettertoUpperCase(dayjs().month(bulletin.mois! - 1).format('MMMM'))} {bulletin.annee}
                                </strong>
                                <Tag color="orange">{bulletin.nbAbsences} absence{bulletin.nbAbsences !== 1 ? 's' : ''}</Tag>
                            </div>
                            <div className="ma-classe-bulletin-card-actions">
                                <Tooltip title={tooltipTitle}>
                                    <Button
                                        type="primary"
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => onModifierBulletin(bulletin.id!)}
                                        disabled={isLocked}
                                    />
                                </Tooltip>
                                <Tooltip title={tooltipTitle}>
                                    <Button
                                        type="primary"
                                        danger
                                        icon={<DeleteOutlined />}
                                        size="small"
                                        onClick={() => onDeleteBulletin(bulletin.id!)}
                                        disabled={isLocked}
                                    />
                                </Tooltip>
                                {getBulletinPdfButton(bulletin.id!)}
                            </div>
                        </div>
                    );
                })
            )}
        </div>
    );

    // ─── Section Résultats ─────────────────────────────────────────────────────
    const renderResultats = () => (
        <div className="ma-classe-mobile-section">
            {elevesEnriched.map((eleve) => (
                <div className="ma-classe-resultat-card" key={eleve.id}>
                    <div className="ma-classe-resultat-card-identity">
                        <strong>{eleve.prenom} {eleve.nom}</strong>
                        <Tag color="geekblue" style={{ fontSize: '11px' }}>{eleve.niveauInterne}</Tag>
                    </div>
                    <Select
                        style={{ width: '100%' }}
                        value={eleve.resultat}
                        options={getResultatOptions()}
                        onChange={(value) => onModifierResultat(eleve.id, value)}
                        placeholder="Sélectionner un résultat"
                    />
                </div>
            ))}
            <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={onEnregistrerResultat}
                block
                size="large"
                style={{
                    marginTop: '12px',
                    backgroundColor: '#52c41a',
                    borderColor: '#52c41a',
                }}
            >
                Enregistrer les résultats
            </Button>
            <div className="ma-classe-taux-info" style={{ marginTop: '10px', justifyContent: 'center' }}>
                <TrophyOutlined style={{ color: '#4FC3F7', marginRight: '6px' }} />
                <span>Taux de réussite : </span>
                <strong style={{ marginLeft: '4px' }}><>{getTauxReussite()}</></strong>
            </div>
        </div>
    );

    // ─── Items collapse ────────────────────────────────────────────────────────
    const collapseItems: CollapseProps['items'] = [
        {
            key: '1',
            label: (
                <span className="ma-classe-collapse-label">
                    <TeamOutlined style={{ marginRight: '8px' }} />
                    Effectif
                    <Tag style={{ marginLeft: '8px', fontSize: '11px', color: '#fff', backgroundColor: 'rgba(255,255,255,0.25)', borderColor: 'rgba(255,255,255,0.4)' }}>
                        {elevesEnriched.length}
                    </Tag>
                </span>
            ),
            children: renderEffectif(),
        },
        {
            key: '2',
            label: (
                <span className="ma-classe-collapse-label">
                    <CalendarOutlined style={{ marginRight: '8px' }} />
                    Présences
                </span>
            ),
            children: renderPresences(),
        },
        {
            key: '3',
            label: (
                <span className="ma-classe-collapse-label">
                    <FileTextOutlined style={{ marginRight: '8px' }} />
                    Bulletins
                </span>
            ),
            children: renderBulletins(),
        },
        {
            key: '4',
            label: (
                <span className="ma-classe-collapse-label">
                    <TrophyOutlined style={{ marginRight: '8px' }} />
                    Résultats
                </span>
            ),
            children: renderResultats(),
        },
    ];

    return (
        <div style={{ padding: '16px' }}>
            {/* Hero mobile */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                paddingBottom: '14px',
                borderBottom: '3px solid #4FC3F7',
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    backgroundColor: '#4FC3F7',
                    flexShrink: 0,
                }}>
                    <ReadOutlined style={{ fontSize: '20px', color: '#ffffff' }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <Typography.Title level={5} style={{ margin: 0, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {classe?.libelle ?? 'Ma Classe'}
                    </Typography.Title>
                    <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        {classe?.debutAnneeScolaire} / {classe?.finAnneeScolaire}
                    </Typography.Text>
                </div>
            </div>

            <Collapse
                accordion
                defaultActiveKey={['1']}
                items={collapseItems}
                className="ma-classe-mobile-collapse"
            />
        </div>
    );
};
